import API_CONFIG from '../config';
import { RoadmapProgressPlayer, RoadmapProgressTask } from '../types';

const PAGE_SIZE = 20;

type FirestoreLeaf = Record<string, unknown>;

/** Firestore REST: integer (string) veya double (sayı) — konsolda "number" görünen de her iki biçimde gelebilir. */
function numField(f: FirestoreLeaf | undefined): number {
  if (!f) return 0;
  if (typeof f.integerValue === 'string') {
    const n = parseInt(f.integerValue, 10);
    return Number.isNaN(n) ? 0 : n;
  }
  if (typeof f.doubleValue === 'number' && !Number.isNaN(f.doubleValue)) {
    return f.doubleValue;
  }
  return 0;
}

/** `completed_tasks` dizisindeki her eleman: `{ mapValue: { fields: { ... } } }` */
function parseTaskMap(
  el: { mapValue?: { fields?: Record<string, FirestoreLeaf> } } | undefined
): RoadmapProgressTask {
  const t = el?.mapValue?.fields ?? {};
  return {
    task_code: (t.task_code as { stringValue?: string } | undefined)?.stringValue ?? '',
    task_title: (t.task_title as { stringValue?: string } | undefined)?.stringValue ?? '',
    task_type: (t.task_type as { stringValue?: string } | undefined)?.stringValue ?? '',
    task_scene: (t.task_scene as { stringValue?: string } | undefined)?.stringValue ?? '',
    quiz_id: (t.quiz_id as { stringValue?: string } | undefined)?.stringValue ?? '',
    chatbot_interaction_type:
      (t.chatbot_interaction_type as { stringValue?: string } | undefined)?.stringValue ?? '',
    required_level: numField(t.required_level),
    session_no: numField(t.session_no),
    session_title: (t.session_title as { stringValue?: string } | undefined)?.stringValue ?? '',
    completed_from_roadmap: (t.completed_from_roadmap as { booleanValue?: boolean } | undefined)?.booleanValue ?? false,
    origin: numField(t.origin),
    destination: numField(t.destination),
    completed_at: (t.completed_at as { timestampValue?: string } | undefined)?.timestampValue
      ? new Date((t.completed_at as { timestampValue: string }).timestampValue)
      : null,
  };
}

/** Döküman adı: .../roadmap_progress/{documentId} — bazı uygulamalar sadece ID kullanır, alan yok. */
function playerIdFromDocumentName(name: string | undefined): string {
  if (!name || typeof name !== 'string') return '';
  const i = name.lastIndexOf('/');
  return (i === -1 ? name : name.slice(i + 1)) || '';
}

function parsePlayerDoc(doc: { name?: string; fields?: Record<string, FirestoreLeaf> }): RoadmapProgressPlayer {
  const f = doc.fields ?? {};
  const fromField = (f.player_id as { stringValue?: string } | undefined)?.stringValue?.trim() ?? '';
  const fromDocName = playerIdFromDocumentName(doc.name);
  const values = (f.completed_tasks as { arrayValue?: { values?: unknown[] } } | undefined)?.arrayValue
    ?.values as { mapValue?: { fields?: Record<string, FirestoreLeaf> } }[] | undefined;
  return {
    player_id: fromField || fromDocName,
    platform: (f.platform as { stringValue?: string } | undefined)?.stringValue ?? '',
    device_model: (f.device_model as { stringValue?: string } | undefined)?.stringValue ?? '',
    app_version: (f.app_version as { stringValue?: string } | undefined)?.stringValue ?? '',
    status: (f.status as { stringValue?: string } | undefined)?.stringValue ?? null,
    created_at: (f.created_at as { timestampValue?: string } | undefined)?.timestampValue
      ? new Date((f.created_at as { timestampValue: string }).timestampValue)
      : null,
    completed_tasks: (values ?? []).map((v) =>
      parseTaskMap(v as { mapValue?: { fields?: Record<string, FirestoreLeaf> } })
    ),
  };
}

function requireApiKey(): string {
  const key = API_CONFIG.FIRESTORE_REST_API_KEY?.trim();
  if (!key) {
    throw new Error(
      'Firestore Web API anahtarı eksik. .env.local içinde REACT_APP_FIRESTORE_API_KEY tanımlayın.'
    );
  }
  return key;
}

function runQueryUrl(project: string, key: string): string {
  return `https://firestore.googleapis.com/v1/projects/${project}/databases/(default)/documents:runQuery?${new URLSearchParams({ key })}`;
}

async function postStructuredQuery(
  project: string,
  key: string,
  structuredQuery: Record<string, unknown>
): Promise<Array<{ document?: { name?: string; fields?: Record<string, FirestoreLeaf> } }>> {
  const res = await fetch(runQueryUrl(project, key), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ structuredQuery }),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Firestore ${res.status}: ${text || res.statusText}`);
  }
  const data = (await res.json()) as Array<{ document?: { name?: string; fields?: Record<string, FirestoreLeaf> } }>;
  return Array.isArray(data) ? data : [];
}

function mapRunQueryToPlayers(rows: Array<{ document?: { name?: string; fields?: Record<string, FirestoreLeaf> } }>) {
  type Doc = { name?: string; fields?: Record<string, FirestoreLeaf> };
  const rawDocs: Doc[] = rows.map((row) => row.document).filter((d): d is Doc => d != null);
  return rawDocs.map((d) => parsePlayerDoc(d));
}

/**
 * `roadmap_progress` — yalnızca `status == "New"` (Firestore `runQuery`).
 * `page` 1 tabanlı; sayfalama `orderBy(__name__)` + `offset`.
 */
export async function fetchRoadmapProgressPage(
  page: number
): Promise<{ players: RoadmapProgressPlayer[]; hasNextPage: boolean }> {
  const key = requireApiKey();
  const project = API_CONFIG.FIRESTORE_PROJECT_ID;
  const pageSafe = Math.max(1, Math.floor(page));
  const offset = (pageSafe - 1) * PAGE_SIZE;

  const structuredQuery: Record<string, unknown> = {
    from: [{ collectionId: 'roadmap_progress' }],
    where: {
      fieldFilter: {
        field: { fieldPath: 'status' },
        op: 'EQUAL',
        value: { stringValue: 'New' },
      },
    },
    orderBy: [{ field: { fieldPath: '__name__' }, direction: 'ASCENDING' }],
    limit: PAGE_SIZE,
  };
  if (offset > 0) {
    structuredQuery.offset = offset;
  }

  const rows = await postStructuredQuery(project, key, structuredQuery);
  const players = mapRunQueryToPlayers(rows);
  return {
    players,
    hasNextPage: players.length === PAGE_SIZE,
  };
}

/**
 * `status == "New"` ve `player_id` tam eşleşme. Önce döküman yolu `roadmap_progress/{id}` dener, sonra runQuery.
 */
/** Tüm `status: New` sayfalarını sırayla çeker (filtre sonrası yerel sayfalama için). Güvenlik üst sınırı. */
const MAX_SCAN_PAGES = 500;

export async function fetchAllRoadmapNewPlayers(maxPages = MAX_SCAN_PAGES): Promise<RoadmapProgressPlayer[]> {
  const all: RoadmapProgressPlayer[] = [];
  for (let page = 1; page <= maxPages; page += 1) {
    const { players, hasNextPage } = await fetchRoadmapProgressPage(page);
    all.push(...players);
    if (!hasNextPage) break;
  }
  return all;
}

export async function fetchRoadmapProgressByPlayerId(playerId: string): Promise<RoadmapProgressPlayer | null> {
  const trimmed = playerId.trim();
  if (!trimmed) return null;
  const key = requireApiKey();
  const project = API_CONFIG.FIRESTORE_PROJECT_ID;
  const enc = encodeURIComponent(trimmed);
  const getUrl = `https://firestore.googleapis.com/v1/projects/${project}/databases/(default)/documents/roadmap_progress/${enc}?${new URLSearchParams({ key })}`;

  const tryGet = async (): Promise<RoadmapProgressPlayer | null> => {
    const res = await fetch(getUrl);
    if (res.status === 404) return null;
    if (!res.ok) {
      const text = await res.text();
      throw new Error(`Firestore ${res.status}: ${text || res.statusText}`);
    }
    const doc = (await res.json()) as { name?: string; fields?: Record<string, FirestoreLeaf> };
    const p = parsePlayerDoc(doc);
    return p.status === 'New' ? p : null;
  };

  const tryQuery = async (): Promise<RoadmapProgressPlayer | null> => {
    const structuredQuery: Record<string, unknown> = {
      from: [{ collectionId: 'roadmap_progress' }],
      where: {
        compositeFilter: {
          op: 'AND',
          filters: [
            {
              fieldFilter: {
                field: { fieldPath: 'status' },
                op: 'EQUAL',
                value: { stringValue: 'New' },
              },
            },
            {
              fieldFilter: {
                field: { fieldPath: 'player_id' },
                op: 'EQUAL',
                value: { stringValue: trimmed },
              },
            },
          ],
        },
      },
      limit: 5,
    };
    const rows = await postStructuredQuery(project, key, structuredQuery);
    const players = mapRunQueryToPlayers(rows);
    return players[0] ?? null;
  };

  try {
    const byPath = await tryGet();
    if (byPath) return byPath;
  } catch {
    /* GET başarısızsa sorguya düş */
  }
  return tryQuery();
}

export const ROADMAP_PROGRESS_PAGE_SIZE = PAGE_SIZE;
